import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  BillingItemTypEnum,
  BillingPlanSettingsDocument,
  type BillingPlanSettingsProjectFragment,
  type BillingPlanSettingsQuery,
  type BillingPlanSettingsQueryVariables,
  type EmailTemplate,
  EmailTemplateTypeEnum,
  IsBillingPlanLockedDocument,
  type IsBillingPlanLockedQuery,
  type IsBillingPlanLockedQueryVariables,
  LockProjectBillingPlanDocument,
  type LockProjectBillingPlanMutation,
  type LockProjectBillingPlanMutationVariables,
  ProjectBillingPlanSettingGenerateInvoiceItemsEnum,
  UpdateProjectBillingPlanSettingsDocument,
  type UpdateProjectBillingPlanSettingsMutation,
  type UpdateProjectBillingPlanSettingsMutationVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { can } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { formatDate, percent } from "@src/utils/formatters";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import type { DateRange } from "@src/utils/types";
import { endOfMonth, startOfMonth } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import { IssueInvoicesForm } from "./issue-invoice-form";
import { SendEmailForm } from "./send-invoice-form";

type IsBillingPlanLockedResponse = NonNullable<
  IsBillingPlanLockedQuery["project"]
>["billingPlanSetting"];

interface EmailWithRecipientsTemplate extends EmailTemplate {
  recipients: string[];
}

export class ProjectBillingPlanSettings implements BaseStore {
  readonly projectId: string;
  appStore;

  @observable.ref
  issueInvoicesForm: IssueInvoicesForm | undefined;

  @observable.ref
  sendInvoicesForm: SendEmailForm | undefined;

  @observable.ref
  sendThankYouForm: SendEmailForm | undefined;

  fetcher = new FetchHelper<
    BillingPlanSettingsQuery,
    BillingPlanSettingsQueryVariables
  >(BillingPlanSettingsDocument);
  private mutator = new MutationHelper<
    UpdateProjectBillingPlanSettingsMutation,
    UpdateProjectBillingPlanSettingsMutationVariables
  >(UpdateProjectBillingPlanSettingsDocument);
  private lockMutator = new MutationHelper<
    LockProjectBillingPlanMutation,
    LockProjectBillingPlanMutationVariables
  >(LockProjectBillingPlanDocument);
  fetcherIsBillingPlanLocked = new FetchHelper<
    IsBillingPlanLockedQuery,
    IsBillingPlanLockedQueryVariables
  >(IsBillingPlanLockedDocument);

  @observable.ref
  newLockedByUser: IsBillingPlanLockedResponse | null = null;

  @observable.ref
  isLockedInterval: NodeJS.Timeout | undefined;

  showBudgetItems = new BooleanState(false);

  @observable
  project: BillingPlanSettingsProjectFragment | undefined;

  @observable
  vatRateOptions: IOption[] = [];

  @observable period: DateRange = {
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  };

  @observable.ref
  emailTemplate: EmailWithRecipientsTemplate | undefined = undefined;

  @observable.ref
  thankYouEmailTemplate: EmailWithRecipientsTemplate | undefined = undefined;

  constructor(projectId: string, appStore: AppStore) {
    makeObservable(this);
    this.projectId = projectId;
    this.appStore = appStore;
  }

  @computed
  get isFetching(): boolean {
    return this.fetcher.isLoading.value || this.mutator.isLoading.value;
  }

  @computed
  get isLocking(): boolean {
    return this.lockMutator.isLoading.value;
  }

  @computed
  get isPlanLocked(): boolean {
    if (!this.project) return true;
    return Boolean(this.project.billingPlanSetting?.locked_at);
  }

  @computed
  get billingItemType(): BillingItemTypEnum {
    return this.showBudgetItems.value
      ? BillingItemTypEnum.BudgetItem
      : BillingItemTypEnum.Category;
  }

  @computed
  get hasError(): boolean {
    return (
      Boolean(this.issueInvoicesForm?.formHasError) ||
      Boolean(this.sendInvoicesForm?.formHasError) ||
      Boolean(this.sendThankYouForm?.formHasError)
    );
  }

  @computed
  get showAccountingColumns(): boolean {
    return (
      can("project_lockBillingPlan") &&
      this.showBudgetItems.value &&
      this.issueInvoicesForm?.$.generateInvoiceItems.value ===
        ProjectBillingPlanSettingGenerateInvoiceItemsEnum.BudgetItemName
    );
  }

  @action.bound
  setPeriod(period: DateRange) {
    this.period = period;
  }

  @action.bound
  setFromQuery(query: BillingPlanSettingsQuery) {
    const settings = query.project?.billingPlanSetting;
    const sendInvoiceTemplate = query.EmailTemplates.find(
      (template) => template.type === EmailTemplateTypeEnum.OutgoingInvoice,
    );
    const sendThankYouTemplate = query.EmailTemplates.find(
      (template) => template.type === EmailTemplateTypeEnum.InvoiceSendThankYou,
    );
    const useEmailFromSettings: boolean =
      Boolean(settings?.email_template?.recipients) &&
      Boolean(settings?.email_template?.subject) &&
      Boolean(settings?.email_template?.content);

    // Set project early so buildDefaultRecipients can access it
    this.project = query.project ?? undefined;

    // Contact email is not available yet (contacts are fetched async in issueInvoicesForm)
    // Recipients will be updated when contact is selected
    const defaultRecipients = this.buildDefaultRecipients(
      query.project?.brand.defaultBillingEntity?.invoice_email,
      undefined,
    );

    this.emailTemplate = sendInvoiceTemplate
      ? {
          id: sendInvoiceTemplate.id,
          type: sendInvoiceTemplate.type,
          subject: sendInvoiceTemplate.subject,
          content: sendInvoiceTemplate.content,
          recipients: defaultRecipients,
        }
      : undefined;

    this.thankYouEmailTemplate = sendThankYouTemplate
      ? {
          id: sendThankYouTemplate.id,
          type: sendThankYouTemplate.type,
          subject: sendThankYouTemplate.subject,
          content: sendThankYouTemplate.content,
          recipients: defaultRecipients,
        }
      : undefined;

    const recipients =
      settings?.email_template?.recipients?.length &&
      settings.email_template.recipients.length > 0
        ? settings.email_template.recipients.join(", ")
        : defaultRecipients.join(", ");

    this.sendInvoicesForm = new SendEmailForm(
      useEmailFromSettings
        ? {
            send_email: settings?.auto_send_invoice ?? false,
            recipients: recipients,
            subject: settings?.email_template?.subject ?? "",
            content: settings?.email_template?.content ?? "",
          }
        : {
            send_email: settings?.auto_send_invoice ?? false,
            recipients:
              defaultRecipients.length > 0 ? defaultRecipients.join(", ") : "",
            subject: sendInvoiceTemplate?.subject ?? "",
            content: sendInvoiceTemplate?.content ?? "",
          },
    );
    this.sendInvoicesForm.setTemplate(this.emailTemplate);

    const useThankYouEmailFromSettings: boolean =
      Boolean(settings?.auto_send_thank_you_template?.recipients) &&
      Boolean(settings?.auto_send_thank_you_template?.subject) &&
      Boolean(settings?.auto_send_thank_you_template?.content);

    this.sendThankYouForm = new SendEmailForm(
      useThankYouEmailFromSettings
        ? {
            send_email: settings?.auto_send_thank_you ?? false,
            recipients:
              settings?.auto_send_thank_you_template?.recipients?.join(", ") ??
              defaultRecipients.join(", "),
            subject: settings?.auto_send_thank_you_template?.subject ?? "",
            content: settings?.auto_send_thank_you_template?.content ?? "",
          }
        : {
            send_email: settings?.auto_send_thank_you ?? false,
            recipients:
              defaultRecipients.length > 0 ? defaultRecipients.join(", ") : "",
            subject: sendThankYouTemplate?.subject ?? "",
            content: sendThankYouTemplate?.content ?? "",
          },
    );
    this.sendThankYouForm.setTemplate(this.thankYouEmailTemplate);

    this.issueInvoicesForm = new IssueInvoicesForm(
      {
        isChecked: settings?.auto_create_invoice ?? false,
        generateInvoiceItems: settings?.generate_invoice_items,
        vatRateId:
          settings?.vat_rate_id ??
          this.appStore.workspaceStore.settings?.defaultVatRate.id ??
          "",
        dateOfSupplyFromIssueDate:
          settings?.date_of_supply_from_issue_date?.toString() ?? "",
        workspace_company_id: settings?.workspace_company_id ?? "",
        workspace_bank_account_id: settings?.workspace_bank_account_id ?? "",
        // invoice_sequence_no: settings?.invoice_sequence_no ?? "",
        billing_entity_id: settings?.billing_entity_id ?? "",
        contact_id: settings?.contact_id ?? "",
        order_id: settings?.order_id ?? "",
        text_on_invoice: settings?.text_on_invoice ?? "",
        note: settings?.note ?? "",
        internal_note: settings?.internal_note ?? "",
        create_draft_invoice: settings?.create_draft_invoice ?? false,
        create_summary_invoice_after_payment:
          settings?.create_summary_invoice_after_payment ?? false,
      },
      this.projectId,
      this.appStore,
    );

    const start = settings?.from ?? query.project?.start_date;
    const end = settings?.to ?? query.project?.expected_end_date;

    this.setPeriod({
      start: startOfMonth(start ? new Date(start) : new Date()),
      end: endOfMonth(end ? new Date(end) : new Date()),
    });
    this.vatRateOptions =
      query.vatRates
        ?.filter((rate) => !rate.deleted)
        .map((rate) => ({
          value: rate.id,
          label: percent.format(rate.amount),
        })) ?? [];

    if (settings?.billing_item_type === BillingItemTypEnum.BudgetItem) {
      this.showBudgetItems.on();
    } else {
      this.showBudgetItems.off();
    }
  }

  @action.bound
  async fetch() {
    const [data, error] = await this.fetcher.fetch(
      {
        projectId: this.projectId,
      },
      undefined,
      true,
    );

    if (error) {
      this.appStore.UIStore.toast({
        title: t`Something went wrong with fetching billing plan, please try again later.`,
        status: "error",
      });
      return;
    }

    this.setFromQuery(data);
  }

  @action.bound
  async update(): Promise<boolean> {
    if (!this.sendInvoicesForm) {
      this.appStore.UIStore.toast({
        title: t`Something went wrong please try reloading the page and try again.`,
        status: "error",
      });
      captureEvent({
        message: "FE: Send invoices form not initialized.",
      });
      return false;
    }

    if (!this.sendThankYouForm) {
      this.appStore.UIStore.toast({
        title: t`Something went wrong please try reloading the page and try again.`,
        status: "error",
      });
      captureEvent({
        message: "FE: Send thank you form not initialized.",
      });
      return false;
    }

    if (!this.issueInvoicesForm) {
      this.appStore.UIStore.toast({
        title: t`Something went wrong please try reloading the page and try again.`,
        status: "error",
      });
      captureEvent({
        message: "FE: Issue invoices form not initialized.",
      });
      return false;
    }

    const issueInvoices = this.issueInvoicesForm.$.isChecked.$;

    const [data, errors] = await this.mutator.mutate({
      input: {
        from: toApiDate(this.period.start),
        to: toApiDate(this.period.end),
        project_id: this.projectId,
        billing_item_type: this.billingItemType,
        vat_rate_id: issueInvoices
          ? this.issueInvoicesForm.$.vatRateId.$
          : null,
        date_of_supply_from_issue_date: issueInvoices
          ? Number(this.issueInvoicesForm.$.dateOfSupplyFromIssueDate.$)
          : null,
        auto_send_invoice: this.showBudgetItems.value
          ? this.sendInvoicesForm.sendEmail.$
          : false,
        email_template:
          this.showBudgetItems.value && this.sendInvoicesForm.sendEmail.$
            ? this.sendInvoicesForm.mutationInput
            : {
                content: "",
                subject: "",
                recipients: [],
              },
        auto_create_invoice: this.showBudgetItems.value
          ? this.issueInvoicesForm.$.isChecked.$
          : false,
        generate_invoice_items:
          this.showBudgetItems.value && issueInvoices
            ? this.issueInvoicesForm.$.generateInvoiceItems.$
            : null,
        create_draft_invoice: this.issueInvoicesForm.$.create_draft_invoice.$,
        billing_entity_id: this.issueInvoicesForm.$.billing_entity_id.$,
        contact_id: this.issueInvoicesForm.$.contact_id.$,
        order_id: this.issueInvoicesForm.$.order_id.$,
        note: this.issueInvoicesForm.$.note.$,
        internal_note: this.issueInvoicesForm.$.internal_note.$,
        workspace_company_id: this.issueInvoicesForm.$.workspace_company_id.$,
        workspace_bank_account_id:
          this.issueInvoicesForm.$.workspace_bank_account_id.$,
        text_on_invoice: this.issueInvoicesForm.$.text_on_invoice.$,
        create_summary_invoice_after_payment:
          this.issueInvoicesForm.$.create_summary_invoice_after_payment.$,
        auto_send_thank_you: this.showBudgetItems.value
          ? this.sendThankYouForm.sendEmail.$
          : false,
        auto_send_thank_you_template:
          this.showBudgetItems.value && this.sendThankYouForm.sendEmail.$
            ? this.sendThankYouForm.mutationInput
            : {
                content: "",
                subject: "",
                recipients: [],
              },
      },
    });

    if (errors) {
      return false;
    }

    const response = {
      project: data.updateProjectBillingPlanSettings,
      EmailTemplates: this.fetcher.getCachedResponse()?.EmailTemplates ?? [],
      vatRates: this.fetcher.getCachedResponse()?.vatRates,
    };

    this.fetcher.cacheResponse(response);
    this.setFromQuery(response);
    return true;
  }

  @action.bound
  revertToCached() {
    const cached = this.fetcher.getCachedResponse();
    if (!cached) return;
    this.setFromQuery(cached);
  }

  @action.bound
  async lockPlan() {
    if (!this.project?.billingPlanSetting) {
      this.appStore.UIStore.toast({
        status: "error",
      });
      captureEvent({
        message: "FE: Project settings for billing plan lock not found",
      });
      return;
    }

    const [data, error] = await this.lockMutator.mutate({
      projectId: this.projectId,
    });

    if (error) return;

    if (data.lockBillingPlanForProject?.billingPlanSetting) {
      this.project.billingPlanSetting.locked_at =
        data.lockBillingPlanForProject.billingPlanSetting.locked_at;
      this.project.billingPlanSetting.lockedByUser =
        data.lockBillingPlanForProject.billingPlanSetting.lockedByUser;
    } else {
      this.project.billingPlanSetting.locked_at = null;
      this.project.billingPlanSetting.lockedByUser = null;
    }
  }

  @action.bound
  setNewLockedByUser(newLockedByUser: IsBillingPlanLockedResponse | null) {
    this.newLockedByUser = newLockedByUser;
  }

  @action.bound
  startPlanLockedChecking() {
    this.isLockedInterval = setInterval(() => {
      this.checkIfBillingPlanLocked();
    }, 15000);
  }

  @action.bound
  private async checkIfBillingPlanLocked() {
    const [data, error] = await this.fetcherIsBillingPlanLocked.fetch(
      {
        projectId: this.projectId,
      },
      undefined,
      true,
      undefined,
      "network-only",
    );

    if (error || !data.project?.billingPlanSetting) {
      this.setNewLockedByUser(null);
      return;
    }

    const newSettings = data.project.billingPlanSetting;
    const currentSettings = this.project?.billingPlanSetting;
    const isLockedBySameUser =
      newSettings.lockedByUser?.id === currentSettings?.lockedByUser?.id;
    const isLockedAtSameTime =
      newSettings.locked_at === currentSettings?.locked_at;

    if (isLockedBySameUser && isLockedAtSameTime) {
      this.setNewLockedByUser(null);
      return;
    }

    this.setNewLockedByUser(data?.project?.billingPlanSetting);
  }

  @computed
  get showLockedWarning(): boolean {
    return !!this.newLockedByUser;
  }

  @computed
  get isLockedWarningDescription(): string | undefined {
    if (!this.newLockedByUser) return undefined;
    if (!this.newLockedByUser.lockedByUser && !this.newLockedByUser.locked_at)
      return t`The invoicing plan was unlocked. Refresh to view changes.`;
    return t`The invoicing plan was locked by ${this.newLockedByUser.lockedByUser?.full_name} on ${formatDate(this.newLockedByUser.locked_at ? new Date(this.newLockedByUser.locked_at) : null)}. Refresh to view changes.`;
  }

  private buildDefaultRecipients(
    billingEmail?: string | null,
    contactEmail?: string | null,
  ): string[] {
    const recipients = new Set<string>();

    if (contactEmail) {
      recipients.add(contactEmail);
    }

    if (billingEmail) {
      // Split by comma in case billing entity has multiple emails
      billingEmail.split(",").forEach((email) => {
        const trimmed = email.trim();
        if (trimmed) recipients.add(trimmed);
      });
    }

    if (this.project?.projectManager.email) {
      recipients.add(this.project.projectManager.email);
    }

    return Array.from(recipients);
  }

  private getDefaultRecipients(): string[] {
    const hasBillingEntitySelected =
      !!this.issueInvoicesForm?.$.billing_entity_id.value;
    const billingEmail = hasBillingEntitySelected
      ? this.issueInvoicesForm?.selectedBillingEntityEmail
      : this.project?.brand.defaultBillingEntity?.invoice_email;
    const contactEmail = this.issueInvoicesForm?.selectedContactEmail;
    return this.buildDefaultRecipients(billingEmail, contactEmail);
  }

  @action.bound
  updateEmailTemplates() {
    const newRecipients = this.getDefaultRecipients().join(", ");

    if (this.emailTemplate) {
      this.emailTemplate.recipients = newRecipients.split(", ");
    }
    if (this.thankYouEmailTemplate) {
      this.thankYouEmailTemplate.recipients = newRecipients.split(", ");
    }

    this.sendInvoicesForm?.setTemplate(this.emailTemplate);
    this.sendThankYouForm?.setTemplate(this.thankYouEmailTemplate);

    this.sendInvoicesForm?.$.recipients.reset(newRecipients);
    this.sendThankYouForm?.$.recipients.reset(newRecipients);
  }

  @action.bound
  replaceContactEmailInRecipients(
    oldEmail?: string | null,
    newEmail?: string | null,
  ) {
    const replaceEmail = (currentValue: string): string => {
      const emails = currentValue
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      // Remove old email if present
      const filtered = oldEmail
        ? emails.filter((e) => e.toLowerCase() !== oldEmail.toLowerCase())
        : emails;

      // Add new email if provided and not already present
      if (
        newEmail &&
        !filtered.some((e) => e.toLowerCase() === newEmail.toLowerCase())
      ) {
        filtered.unshift(newEmail);
      }

      return filtered.join(", ");
    };

    if (this.sendInvoicesForm?.sendEmail.$) {
      const updated = replaceEmail(this.sendInvoicesForm.$.recipients.value);
      this.sendInvoicesForm.$.recipients.onChange(updated);
    }

    if (this.sendThankYouForm?.sendEmail.$) {
      const updated = replaceEmail(this.sendThankYouForm.$.recipients.value);
      this.sendThankYouForm.$.recipients.onChange(updated);
    }

    // Update templates as well
    if (this.emailTemplate) {
      this.emailTemplate.recipients = replaceEmail(
        this.emailTemplate.recipients.join(", "),
      ).split(", ");
    }
    if (this.thankYouEmailTemplate) {
      this.thankYouEmailTemplate.recipients = replaceEmail(
        this.thankYouEmailTemplate.recipients.join(", "),
      ).split(", ");
    }

    this.sendInvoicesForm?.setTemplate(this.emailTemplate);
    this.sendThankYouForm?.setTemplate(this.thankYouEmailTemplate);
  }
}
